import { h, Component, Fragment } from "preact";
import { observer } from "mobx-react";
import { RecommendationProfileTracker, RecommendationResultTracker } from '@searchspring/snap-preact-components';

import { Result } from '../../Result/Result';

import { ArrowUp } from "../../Icons/ArrowUp";
import { ArrowDown } from "../../Icons/ArrowDown";
import { WcRecsCardFooter } from './WcRecsCardFooter';

import './recs.css';

@observer
export class Recs extends Component {
  constructor(props) {
    super();

    const controller = props.controller;

    if (!controller.store.profile) {
      controller.init();
      controller.search();
    }

    this.state = { totalPages: 1, currentPage: 0, showWcRecsCta: false };
    this._lastResultsLength = undefined;

    this.next = this.next.bind(this);
    this.previous = this.previous.bind(this);
  }

  getRecsDomId = () => `cql-carousel-recommendation-${this.props.controller.id}`;

  syncWcRecsCtaVisibility = () => {
    const controller = this.props.controller;
    const type = controller.context?.options?.type;
    if (type === 'cart-drawer') {
      if (this.state.showWcRecsCta) this.setState({ showWcRecsCta: false });
      return;
    }
    const ctx = controller.context || {};
    const fromContext = !!(ctx.options?.wcRecsCartActions || ctx.custom?.wcRecsCartActions);
    const root = document.getElementById(this.getRecsDomId());
    const fromDom = !!(root && root.closest && root.closest('[data-wc-recs-cart-actions]'));
    const show = fromContext || fromDom;
    if (show !== this.state.showWcRecsCta) this.setState({ showWcRecsCta: show });
  };

  updateSlidesAnchorsTabIndexes = () => {
    const container = document.getElementById(this.getRecsDomId());
    if (!container) return;
    for (const el of container.querySelectorAll(
      '.swiper-slide:not(.swiper-slide-visible) a, .swiper-slide:not(.swiper-slide-visible) button'
    )) {
      el.setAttribute('tabindex', '-1');
    }
    for (const el of container.querySelectorAll(
      '.swiper-slide.swiper-slide-visible a, .swiper-slide.swiper-slide-visible button'
    )) {
      el.removeAttribute('tabindex');
    }
  };

  updateNavigationState = () => {
    const swiper = this.swiper;
    if (!swiper) return;
    const type = this.props.controller.context?.options?.type;
    const isCartDrawer = type === 'cart-drawer';
    if (!isCartDrawer) return;
    const prevEl = document.querySelector(`#${this.getRecsDomId()} .recs-prev`);
    const nextEl = document.querySelector(`#${this.getRecsDomId()} .recs-next`);
    if (!prevEl || !nextEl) return;
    const { isBeginning, isEnd } = swiper;
    const singlePage = swiper.slides.length <= swiper.params.slidesPerView;
    prevEl.disabled = singlePage || isBeginning;
    nextEl.disabled = singlePage || isEnd;
  };

  initSwiper = () => {
    const controller = this.props.controller;
    const carouselId = this.getRecsDomId();
    const container = document.getElementById(carouselId);
    if (!container || typeof window.Swiper === 'undefined') return;

    const swiperEl = container.querySelector('.swiper');
    if (!swiperEl || this.swiper) return;

    const type = controller.context?.options?.type;
    const isCartDrawer = type === 'cart-drawer';
    const navSuffix = controller.id;

    if (isCartDrawer) {
      const slidesPerView = 1.33;
      const breakpoints = { 0: { slidesPerView: 1.33, slidesPerGroup: 1 } };

      this.swiper = new window.Swiper(swiperEl, {
        spaceBetween: 0,
        slidesPerView,
        slidesPerGroup: 1,
        breakpoints,
        watchSlidesProgress: true,
        pagination: false,
        observer: true,
        observeParents: true,
        on: {
          init: () => {
            this.updateNavigationState();
            this.updateSlidesAnchorsTabIndexes();
            this.syncPagePaginationState();
            requestAnimationFrame(() => {
              if (this.swiper) {
                this.syncPagePaginationState();
                this.updateNavigationState();
              }
            });
          },
          slideChange: () => {
            this.updateNavigationState();
            this.updateSlidesAnchorsTabIndexes();
            this.syncPagePaginationState();
          },
          resize: () => {
            this.updateNavigationState();
            this.updateSlidesAnchorsTabIndexes();
            this.syncPagePaginationState();
          },
        },
      });
    } else {
      this.swiper = new window.Swiper(swiperEl, {
        spaceBetween: 5,
        slidesPerView: 1,
        slidesPerGroup: 1,
        watchOverflow: true,
        watchSlidesProgress: true,
        observer: true,
        observeParents: true,
        navigation: {
          nextEl: container.querySelector(`.swiper-button-next-recs-${navSuffix}`),
          prevEl: container.querySelector(`.swiper-button-prev-recs-${navSuffix}`),
        },
        scrollbar: {
          el: container.querySelector(`.swiper-scrollbar-recs-${navSuffix}`),
          draggable: true,
        },
        breakpoints: {
          750: { slidesPerView: 3, slidesPerGroup: 1, spaceBetween: 5 },
          990: { slidesPerView: 4, slidesPerGroup: 1, spaceBetween: 5 },
          1200: { slidesPerView: 6, slidesPerGroup: 1, spaceBetween: 5 },
        },
        on: {
          init: () => {
            swiperEl.classList.add('loaded');
            this.updateSlidesAnchorsTabIndexes();
            requestAnimationFrame(() => {
              if (this.swiper) this.swiper.update();
            });
          },
          slideChange: () => {
            this.updateSlidesAnchorsTabIndexes();
          },
          resize: () => {
            this.updateSlidesAnchorsTabIndexes();
          },
        },
      });
    }

    this.updateNavigationState();
    this.updateSlidesAnchorsTabIndexes();
    if (isCartDrawer) this.syncPagePaginationState();
    this._lastResultsLength = controller.store?.results?.length ?? 0;
    container.dispatchEvent(new CustomEvent('recsSwiperReady', { bubbles: true }));
  };

  getSlidesPerGroup = () => {
    const swiper = this.swiper;
    if (!swiper) return 4;
    const g = swiper.params.slidesPerGroup;
    return typeof g === 'number' ? Math.max(1, Math.floor(g)) : 4;
  };

  syncPagePaginationState = () => {
    const swiper = this.swiper;
    if (!swiper) return;
    const totalSlides = swiper.slides.length;
    const perPage = this.getSlidesPerGroup();
    const totalPages = Math.max(1, Math.ceil(totalSlides / perPage));
    const currentPage = Math.min(Math.floor(swiper.activeIndex / perPage), totalPages - 1);
    this.setState({ totalPages, currentPage });
  };

  componentDidMount() {
    this.syncWcRecsCtaVisibility();
    if (typeof window.Swiper !== 'undefined') {
      this.initSwiper();
    } else {
      document.addEventListener('swiperReady', this.initSwiper, { once: true });
    }
    requestAnimationFrame(() => this.syncWcRecsCtaVisibility());
    this._swiperRevealFallback = window.setTimeout(() => {
      const root = document.getElementById(this.getRecsDomId());
      const swiperEl = root?.querySelector('.swiper-wrapper-loader .swiper');
      if (swiperEl && !swiperEl.classList.contains('loaded')) {
        swiperEl.classList.add('loaded');
      }
    }, 10000);
  }

  componentDidUpdate() {
    this.syncWcRecsCtaVisibility();
    const swiper = this.swiper;
    if (!swiper) return;
    const currLen = this.props.controller?.store?.results?.length ?? 0;
    const isCartDrawer = this.props.controller.context?.options?.type === 'cart-drawer';
    if (this._lastResultsLength !== undefined && this._lastResultsLength !== currLen) {
      swiper.update();
      if (isCartDrawer) {
        this.syncPagePaginationState();
        this.updateNavigationState();
      } else {
        this.updateSlidesAnchorsTabIndexes();
      }
    }
    this._lastResultsLength = currLen;
  }

  componentWillUnmount() {
    if (this._swiperRevealFallback) {
      clearTimeout(this._swiperRevealFallback);
      this._swiperRevealFallback = null;
    }
    document.removeEventListener('swiperReady', this.initSwiper);
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  next() {
    this.swiper?.slideNext();
  }

  previous() {
    this.swiper?.slidePrev();
  }

  render() {
    const controller = this.props.controller;
    const type = controller.context?.options?.type;
    const isCartDrawer = type === 'cart-drawer';
    const store = controller?.store;
    const params = store.profile?.display?.templateParameters;
    const config = store.config;

    const title = params?.title || null;
    const tag = config?.tag || 'unknown';
    const maxProductsToShow = params?.maxProductsToShow || 12;
    const hideRatings = false;
    let hideSwatches = false;
    let hideMessages = false;
    const hideTitle = true;

    switch (tag) {
      case 'cross-sell':
        hideSwatches = true;
        hideMessages = true;
        break;
    }

    const carouselId = this.getRecsDomId();
    const navSuffix = controller.id;
    const ctx = controller.context || {};
    const recsProductCardClass =
      ctx.options?.wcRecsProductCardClass ||
      (ctx.custom && typeof ctx.custom === 'object' ? ctx.custom.wcRecsProductCardClass : undefined);
    const showWcCtaFooter = this.state.showWcRecsCta === true;

    const slideList = store.results
      .filter((_, i) => i < maxProductsToShow)
      .map((result) => ({
        id: result.id,
        content: (
          <RecommendationResultTracker controller={controller} result={result}>
            <div class="wc-recs-tracker-inner">
              <Result
                isRecommendations
                result={result}
                hideRatings={hideRatings}
                hideSwatches={hideSwatches}
                hideMessages={hideMessages}
                hidePricing
                className={recsProductCardClass}
              />
              {showWcCtaFooter ? (
                <div class="wc-recs-card-cta-wrap">
                  <WcRecsCardFooter result={result} />
                </div>
              ) : null}
            </div>
          </RecommendationResultTracker>
        ),
      }));

    return (
      <div
        id={carouselId}
        class={`ss__recs cql-carousel--contain cql-carousel-recommendation--contain container${isCartDrawer ? '' : ' ss__recs--site-swiper'}`}
      >
        {!hideTitle && <h3 class="cql-carousel-recommendation--title h1">{title}</h3>}
        <RecommendationProfileTracker controller={controller}>
          {isCartDrawer ? (
            <>
              <div class="swiper glider cql-carousel cql-carousel-recommendation">
                <div class="swiper-wrapper">
                  {slideList.map(({ id, content }) => (
                    <div class="swiper-slide cql-carousel-recommendation__slide" key={id}>
                      {content}
                    </div>
                  ))}
                </div>
              </div>
              <div class="cql-carousel--navigation cql-carousel--navigation-dots">
                <button type="button" class="recs-prev cql-carousel--arrow cql-carousel--prev" aria-label="Previous Slide" onClick={this.previous}>
                  <ArrowUp />
                </button>
                <div class="recs-pagination swiper-pagination">
                  {Array.from({ length: this.state.totalPages }, (_, i) => (
                    <button
                      type="button"
                      key={`page-${i}`}
                      class={`swiper-pagination-bullet${i === this.state.currentPage ? ' swiper-pagination-bullet-active' : ''}`}
                      aria-label={`Go to page ${i + 1}`}
                      data-page-index={i}
                      onClick={() => {
                        if (this.swiper) this.swiper.slideTo(i * this.getSlidesPerGroup());
                      }}
                    />
                  ))}
                </div>
                <button type="button" class="recs-next cql-carousel--arrow cql-carousel--next" aria-label="Next Slide" onClick={this.next}>
                  <ArrowDown />
                </button>
              </div>
            </>
          ) : (
            <div class="swiper-wrapper-loader ss__recs-swiper-loader">
              <div class="swiper glider cql-carousel cql-carousel-recommendation">
                <div class="swiper-wrapper">
                  {slideList.map(({ id, content }) => (
                    <div class="swiper-slide cql-carousel-recommendation__slide" key={id}>
                      {content}
                    </div>
                  ))}
                </div>
              </div>
              <div class={`swiper-scrollbar swiper-scrollbar-recs-${navSuffix}`} />
              <div class={`swiper-button-prev swiper-button-prev-recs-${navSuffix}`} />
              <div class={`swiper-button-next swiper-button-next-recs-${navSuffix}`} />
            </div>
          )}
        </RecommendationProfileTracker>
      </div>
    );
  }
}
